// Global Instructions Rule Applied!
//
// Agent Access — "Agent activity" tab orchestration (Phase 4 propose-approve).
// Session-scoped settings data (small, refetched after every action) — no
// Redux slice, mirroring useAgentGrants.
//
// The list + stat strip are this screen's; the three verbs live in
// useProposalActions, which the admin Agent Oversight screen uses too against
// its own list. Splitting them is what keeps an admin's approve identical to
// the owner's rather than a second implementation of the same sequence.
import { useCallback, useEffect, useState } from 'react'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  fetchMyAgentProposals,
  fetchAgentActivitySummary
} from '../services/agentAccessService.ts'
import { useProposalActions } from './useProposalActions.ts'
import type {
  AgentActivitySummary,
  AgentProposal
} from '../../../state_manager/data_models/agent_access_model.ts'

export const useAgentProposals = () => {
  const [proposals, setProposals] = useState<AgentProposal[]>([])
  const [summary, setSummary] = useState<AgentActivitySummary | null>(null)
  const [loading, setLoading] = useState(true)

  // One refresh covers the list AND the stat strip, so the Refresh button and
  // every approve/reject/undo keep the "Pending review" number in sync with
  // the rows below it.
  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const [res, summaryRes] = await Promise.all([
        fetchMyAgentProposals(token),
        fetchAgentActivitySummary(token)
      ])
      if (res.error) showErrorToast(res.error)
      if (summaryRes.error) showErrorToast(summaryRes.error)
      setProposals(res.data)
      setSummary(summaryRes.data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const { reviewingId, review, retry, undo } = useProposalActions(refresh)

  return { proposals, summary, loading, reviewingId, refresh, review, retry, undo }
}
